/**
 * Browser-side parser for MoGe2 scene point payloads produced by the
 * camera-control scene estimator. Must stay in sync with the canonical
 * services/camera-controller/scenePoints.js parser.
 *
 * Binary layouts, little-endian:
 *
 * SCN1 (16-byte records):
 *   uint32 magic 0x53434e31 ('SCN1') | uint32 pointCount | uint32 flags | uint32 reserved
 *   pointCount x: float32 x, y, z | uint8 r, g, b, a
 *
 * SCN2 (24-byte records):
 *   uint32 magic 0x53434e32 ('SCN2') | uint32 pointCount | uint32 flags | uint32 reserved
 *   pointCount x: float32 x, y, z, confidence | uint8 r, g, b, a | uint8 source | uint8 reserved[3]
 */

export const SCENE_MAGIC = 0x53434e31
export const SCENE_V2_MAGIC = 0x53434e32
export const SCENE_HEADER_BYTES = 16
export const SCENE_RECORD_BYTES = 16
export const SCENE_V2_RECORD_BYTES = 24
export const SCENE_MAX_POINTS = 4_000_000

export interface ScenePoints {
  positions: Float32Array
  colors: Uint8Array
  pointCount: number
  confidences?: Float32Array
  sources?: Uint8Array
  version?: 1 | 2
}

/**
 * Why a scene failed to load. `truncated` and `header-too-small` are both short
 * bodies — a transport failure worth retrying. `bad-magic` and
 * `point-count-exceeded` mean the bytes that arrived are not a scene, and
 * `http-error` means the server refused, so re-asking cannot help.
 */
export type ScenePointsFailureReason =
  'truncated' | 'header-too-small' | 'bad-magic' | 'point-count-exceeded' | 'http-error'

/** Short bodies: the same request can still produce the whole payload. */
const RETRYABLE_SCENE_FAILURES: ReadonlySet<ScenePointsFailureReason> = new Set(['truncated', 'header-too-small'])

/**
 * One stable line per reason. The constructor derives the message from the
 * reason so no call site can interpolate a byte count, magic number or status
 * into it: those are fields, which Sentry reads and no display path can render
 * (CLAUDE.md -> "Error messages: one field, one audience"). Stable text also
 * keeps Sentry grouping one issue per reason instead of one per byte count.
 */
const SCENE_FAILURE_MESSAGES: Record<ScenePointsFailureReason, string> = {
  truncated: 'Scene payload truncated',
  'header-too-small': 'Scene payload shorter than its header',
  'bad-magic': 'Invalid scene magic',
  'point-count-exceeded': 'Scene point count exceeds limit',
  'http-error': 'Scene points request failed',
}

/**
 * A scene the loader refused, from either the response or the parser. Every
 * diagnostic lives on the instance; the message says only which check failed.
 */
export class ScenePointsLoadError extends Error {
  reason: ScenePointsFailureReason
  receivedBytes: number
  expectedBytes?: number
  pointCount?: number
  magic?: number
  status?: number
  /** How many fetch attempts were made before giving up (set by `fetchScenePoints`). */
  attempts?: number

  constructor(details: {
    reason: ScenePointsFailureReason
    receivedBytes: number
    expectedBytes?: number
    pointCount?: number
    magic?: number
    status?: number
  }) {
    super(SCENE_FAILURE_MESSAGES[details.reason])
    this.name = 'ScenePointsLoadError'
    this.reason = details.reason
    this.receivedBytes = details.receivedBytes
    this.expectedBytes = details.expectedBytes
    this.pointCount = details.pointCount
    this.magic = details.magic
    this.status = details.status
  }
}

export function isScenePointsLoadError(error: unknown): error is ScenePointsLoadError {
  return error instanceof ScenePointsLoadError
}

/** Backoff between scene re-fetches. Length = the retry budget. */
export const SCENE_FETCH_RETRY_DELAYS_MS = [400, 1_200]

export function parseScenePoints(buffer: ArrayBuffer): ScenePoints {
  const bytes = new Uint8Array(buffer)
  if (bytes.byteLength < SCENE_HEADER_BYTES) {
    throw new ScenePointsLoadError({
      reason: 'header-too-small',
      receivedBytes: bytes.byteLength,
      expectedBytes: SCENE_HEADER_BYTES,
    })
  }

  const header = new DataView(bytes.buffer, bytes.byteOffset, SCENE_HEADER_BYTES)
  const magic = header.getUint32(0, true)
  const version: 1 | 2 | 0 = magic === SCENE_V2_MAGIC ? 2 : magic === SCENE_MAGIC ? 1 : 0
  if (!version) {
    throw new ScenePointsLoadError({ reason: 'bad-magic', receivedBytes: bytes.byteLength, magic })
  }

  const pointCount = header.getUint32(4, true)
  if (pointCount > SCENE_MAX_POINTS) {
    throw new ScenePointsLoadError({
      reason: 'point-count-exceeded',
      receivedBytes: bytes.byteLength,
      pointCount,
    })
  }

  const recordBytes = version === 2 ? SCENE_V2_RECORD_BYTES : SCENE_RECORD_BYTES
  const expectedBytes = SCENE_HEADER_BYTES + pointCount * recordBytes
  if (bytes.byteLength < expectedBytes) {
    throw new ScenePointsLoadError({
      reason: 'truncated',
      receivedBytes: bytes.byteLength,
      expectedBytes,
      pointCount,
    })
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset + SCENE_HEADER_BYTES, pointCount * recordBytes)
  const positions = new Float32Array(pointCount * 3)
  const colors = new Uint8Array(pointCount * 4)
  const confidences = new Float32Array(pointCount)
  const sources = new Uint8Array(pointCount)

  for (let i = 0; i < pointCount; i += 1) {
    const offset = i * recordBytes
    positions[i * 3] = view.getFloat32(offset, true)
    positions[i * 3 + 1] = view.getFloat32(offset + 4, true)
    positions[i * 3 + 2] = view.getFloat32(offset + 8, true)
    if (version === 2) {
      confidences[i] = view.getFloat32(offset + 12, true)
      colors[i * 4] = view.getUint8(offset + 16)
      colors[i * 4 + 1] = view.getUint8(offset + 17)
      colors[i * 4 + 2] = view.getUint8(offset + 18)
      colors[i * 4 + 3] = view.getUint8(offset + 19)
      sources[i] = view.getUint8(offset + 20)
    } else {
      confidences[i] = 1
      colors[i * 4] = view.getUint8(offset + 12)
      colors[i * 4 + 1] = view.getUint8(offset + 13)
      colors[i * 4 + 2] = view.getUint8(offset + 14)
      colors[i * 4 + 3] = view.getUint8(offset + 15)
      sources[i] = 0
    }
  }

  return { positions, colors, pointCount, confidences, sources, version }
}

function delayScenePointsRetry(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    function onAbort() {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Fetch and parse a scene payload, retrying a short body.
 *
 * A short response is a transport failure, not a bad asset: what did arrive
 * parsed fine, the body was just cut off in flight. Retries bypass the HTTP
 * cache so a stored partial response can't be replayed. Everything else — a bad
 * magic, an over-limit point count, an HTTP status, an abort — fails on the
 * first attempt, since re-asking cannot change the answer.
 */
export async function fetchScenePoints(
  url: string,
  options?: { signal?: AbortSignal; onRetry?: (error: ScenePointsLoadError, attempt: number) => void },
): Promise<ScenePoints> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const response = await fetch(url, {
        signal: options?.signal,
        ...(attempt > 0 ? { cache: 'reload' as RequestCache } : {}),
      })
      if (!response.ok) {
        throw new ScenePointsLoadError({ reason: 'http-error', receivedBytes: 0, status: response.status })
      }
      return parseScenePoints(await response.arrayBuffer())
    } catch (error) {
      const retryDelay = SCENE_FETCH_RETRY_DELAYS_MS[attempt]
      if (!isScenePointsLoadError(error) || !RETRYABLE_SCENE_FAILURES.has(error.reason)) throw error
      // An abort that lands mid-retry is an abort, not a payload failure: callers
      // key off AbortError to stay silent, so they must not get the parser's error.
      options?.signal?.throwIfAborted()
      if (retryDelay == null) {
        error.attempts = attempt + 1
        throw error
      }
      options?.onRetry?.(error, attempt + 1)
      await delayScenePointsRetry(retryDelay, options?.signal)
      options?.signal?.throwIfAborted()
    }
  }
}
